import { DayType } from '@/app/constants/dayType'
import {
  DAY_TYPE_BORDER_COLORS,
  DAY_TYPE_COLORS,
} from '@/modules/drivers/tabs/registry/schedule/constants/DayColors'


export type DayTypePaletteId = 'classic' | 'bold'

export type DayTypeFamily =
  | 'none'
  | 'work'
  | 'availability'
  | 'free'
  | 'freeFor'
  | 'vacation'
  | 'otherLeave'
  | 'sick'
  | 'benefit'
  | 'absence'
  | 'military'
  | 'noContract'

export type DayTypeVisual = {
  background: string
  border: string
  text: string
  borderWidth: number
}

export const DAY_TYPE_FAMILIES: Record<DayTypeFamily, DayType[]> = {
  none: [DayType.NONE],

  work: [DayType.P, DayType.Ps],

  availability: [DayType.D, DayType.Prz],

  free: [DayType.W, DayType.Wo, DayType.Pw],

  freeFor: [
    DayType.Xw,
    DayType.Xb,
    DayType.Xd,
    DayType.Wn,
    DayType.Wx,
    DayType.Ws,
    DayType.Wu,
  ],

  vacation: [DayType.U, DayType.Uz, DayType.Uo, DayType.MergedU, DayType.U5],

  otherLeave: [
    DayType.Ui,
    DayType.Un,
    DayType.Ur,
    DayType.Ux,
    DayType.Us,
    DayType.Um,
    DayType.Uf,
    DayType.U2,
    DayType.Uoj,
    DayType.WoUoj,
    DayType.Bp,
    DayType.Bw,
    DayType.M,
    DayType.WoM,
    DayType.D1,
    DayType.D2,
    DayType.S2,
  ],

  sick: [
    DayType.C,
    DayType.Cn,
    DayType.Cb,
    DayType.MergedC,
    DayType.WoC,
    DayType.WoCn,
    DayType.WoCb,
  ],

  benefit: [
    DayType.Z,
    DayType.Zb,
    DayType.Zs,
    DayType.Zw,
    DayType.Zn,
    DayType.Od,
    DayType.Or,
    DayType.Os,
    DayType.Oo,
    DayType.Rc,
    DayType.Rw,
    DayType.Rb,
    DayType.WoZ,
    DayType.WoZb,
    DayType.WoZs,
    DayType.WoZw,
    DayType.WoZn,
    DayType.WoOd,
    DayType.WoOr,
    DayType.WoOs,
    DayType.WoOo,
    DayType.WoRc,
    DayType.WoRw,
    DayType.WoRb,
  ],

  absence: [DayType.Nn, DayType.Np, DayType.Nu],

  military: [DayType.Sw],

  noContract: [DayType.NoContract],
}

export const DAY_TYPE_FAMILY_LABELS: Record<DayTypeFamily, string> = {
  none: 'Brak rodzaju dnia',
  work: 'Praca',
  availability: 'Dyspozycyjność / przestój',
  free: 'Dni wolne',
  freeFor: 'Wolne za / dzień wolny udzielony',
  vacation: 'Urlop wypoczynkowy',
  otherLeave: 'Pozostałe urlopy i zwolnienia',
  sick: 'Chorobowe',
  benefit: 'Zasiłki i opieka',
  absence: 'Nieobecności',
  military: 'Służba wojskowa',
  noContract: 'Brak umowy',
}

/** Rodzina, do której należy dany rodzaj dnia (szybka mapa odwrotna). */
const FAMILY_BY_DAY_TYPE: Record<number, DayTypeFamily> = (() => {
  const map: Record<number, DayTypeFamily> = {}
  ;(Object.keys(DAY_TYPE_FAMILIES) as DayTypeFamily[]).forEach((family) => {
    DAY_TYPE_FAMILIES[family].forEach((dayType) => {
      map[dayType] = family
    })
  })
  return map
})()

export const getDayTypeFamily = (dayType: DayType): DayTypeFamily =>
  FAMILY_BY_DAY_TYPE[dayType] ?? 'none'

type FamilySpec = Omit<DayTypeVisual, 'borderWidth'>

type PaletteDefinition = {
  label: string
  borderWidth: number
  families: Record<DayTypeFamily, FamilySpec>
}


const BOLD: PaletteDefinition = {
  label: 'Wyraźna',
  borderWidth: 3,
  families: {
    none: { background: '#E2E8F0', border: '#B6C2D2', text: '#374151' },
    work: { background: '#16A34A', border: '#15803D', text: '#FFFFFF' },
    availability: { background: '#BBF7D0', border: '#34A77A', text: '#14532D' },
    free: { background: '#1D4ED8', border: '#15317E', text: '#FFFFFF' },
    freeFor: { background: '#4B86E8', border: '#1D4ED8', text: '#FFFFFF' },
    vacation: { background: '#B45309', border: '#7C3A05', text: '#FFFFFF' },
    otherLeave: { background: '#D98C0B', border: '#A3670A', text: '#FFFFFF' },
    sick: { background: '#DC2626', border: '#991B1B', text: '#FFFFFF' },
    benefit: { background: '#7E22CE', border: '#581C87', text: '#FFFFFF' },
    absence: { background: '#DB2777', border: '#9D174D', text: '#FFFFFF' },
    military: { background: '#64748B', border: '#475569', text: '#FFFFFF' },
    noContract: { background: '#CBD5E1', border: '#94A3B8', text: '#334155' },
  },
}

const ALTERNATIVE_PALETTES: Record<
  Exclude<DayTypePaletteId, 'classic'>,
  PaletteDefinition
> = {
  bold: BOLD,
}

const CLASSIC_TEXT = '#1F2937'


export const getDayTypeVisual = (
  paletteId: DayTypePaletteId,
  dayType: DayType,
): DayTypeVisual => {
  const palette = (
    ALTERNATIVE_PALETTES as Partial<Record<DayTypePaletteId, PaletteDefinition>>
  )[paletteId]


  if (!palette) {
    return {
      background: DAY_TYPE_COLORS[dayType] ?? 'transparent',
      border: DAY_TYPE_BORDER_COLORS[dayType] ?? 'transparent',
      text: CLASSIC_TEXT,
      borderWidth: 2,
    }
  }

  const spec = palette.families[getDayTypeFamily(dayType)]
  return { ...spec, borderWidth: palette.borderWidth }
}

export type DayTypePaletteOption = {
  id: DayTypePaletteId
  label: string
  swatches: DayTypeVisual[]
}

const PREVIEW_FAMILIES: DayTypeFamily[] = [
  'free',
  'vacation',
  'sick',
  'absence',
  'work',
]

const PREVIEW_DAY_TYPES = PREVIEW_FAMILIES.map(
  (family) => DAY_TYPE_FAMILIES[family][0],
)

const buildSwatches = (id: DayTypePaletteId): DayTypeVisual[] =>
  PREVIEW_DAY_TYPES.map((dayType) => getDayTypeVisual(id, dayType))

export const DAY_TYPE_PALETTE_OPTIONS: DayTypePaletteOption[] = [
  {
    id: 'classic',
    label: 'Klasyczna',
    swatches: buildSwatches('classic'),
  },
  ...(
    Object.keys(ALTERNATIVE_PALETTES) as Exclude<DayTypePaletteId, 'classic'>[]
  ).map((id) => ({
    id,
    label: ALTERNATIVE_PALETTES[id].label,
    swatches: buildSwatches(id),
  })),
]

export const LEGEND_FAMILY_ORDER: DayTypeFamily[] = [
  'work',
  'availability',
  'free',
  'freeFor',
  'vacation',
  'otherLeave',
  'sick',
  'benefit',
  'absence',
  'military',
  'noContract',
]
